import {
  Decoration,
  DecorationSet,
  EditorView,
  MatchDecorator,
  ViewPlugin,
  ViewUpdate,
  WidgetType,
} from "@codemirror/view";
import { Channel, ServerMember, ServerRole, User } from "stoat.js";

import { useClient } from "@revolt/client";
import {
  RE_UNICODE_EMOJI,
  unicodeEmojiUrl,
} from "@revolt/markdown/emoji/UnicodeEmoji";
import { userInformation } from "@revolt/markdown/users";
import { useSmartParams } from "@revolt/routing";

import {
  GameTag,
  gameTagUrl,
  isGameTagKind,
  lookupGameTag,
} from "@revolt/markdown/plugins/gameTagData";
import { parseUnicodeEmoji } from "@revolt/markdown/plugins/unicodeEmoji";
import { isInCodeBlock } from "./codeMirrorCommon";

export function codeMirrorWidgets() {
  const getClient = useClient();
  const params = useSmartParams();

  const widgetMatcher = new MatchDecorator({
    regexp: new RegExp(
      RE_UNICODE_EMOJI.source +
        "|" +
        /:([0-7][0-9A-HJKMNP-TV-Z]{25}):|<@([0-7][0-9A-HJKMNP-TV-Z]{25})>|<#([0-7][0-9A-HJKMNP-TV-Z]{25})>|<%([0-7][0-9A-HJKMNP-TV-Z]{25})>/
          .source +
        "|" +
        // Kind is matched loosely and sifted by the table below, which is what
        // makes the whole thing case-insensitive without spelling every letter
        // out in a pattern this shared regex has no flag for.
        /@([a-zA-Z]+)[ \t]+([a-zA-Z][a-zA-Z-]*)/.source,
      "g",
    ),
    decoration: (
      [
        str,
        unicodeEmoji,
        emojiId,
        userId,
        channelId,
        roleId,
        tagKind,
        tagValue,
      ],
      view,
      pos,
    ) => {
      if (isInCodeBlock(view.state, pos, pos + str[0].length)) {
        return null;
      }

      const client = getClient();
      const { serverId } = params();

      let widget: WidgetType = null!;

      if (unicodeEmoji) {
        const { str, pack } = parseUnicodeEmoji(unicodeEmoji);

        widget = new EmojiWidget(unicodeEmojiUrl(pack, str));
      } else if (emojiId) {
        widget = new EmojiWidget(
          `${client?.configuration?.features.autumn.url}/emojis/${emojiId}`,
        );
      } else if (userId) {
        const member = serverId
          ? getClient().serverMembers.getByKey({
              server: serverId,
              user: userId,
            })
          : undefined;

        const user = getClient().users.get(userId);

        widget = new UserMentionWidget(user, member);
      } else if (channelId) {
        const channel = getClient().channels.get(channelId);

        if (channel) {
          widget = new ChannelMentionWidget(channel);
        }
      } else if (roleId) {
        const role = getClient().servers.get(serverId!)?.roles.get(roleId);

        if (role) {
          widget = new RoleMentionWidget(role);
        }
      } else if (tagKind) {
        const tag = lookupGameTag(tagKind, tagValue);

        // Returns rather than falls through: the pattern matches any `@word
        // word`, so most of what reaches here is ordinary prose that must be
        // left as the text it is.
        if (!tag) return null;

        // Not while the caret is in it. Short forms resolve - `pal` is already
        // a Paladin - so a tag became one atomic chip halfway through being
        // typed and the rest of the word had nowhere to go. The draft styling
        // below takes over for as long as it is being written.
        if (touchesSelection(view, pos, pos + str.length)) return null;

        widget = new GameTagWidget(tag);
      }

      return Decoration.replace({
        widget,
      });
    },
  });

  const widgetsPlugin = ViewPlugin.fromClass(
    class {
      placeholders: DecorationSet;
      constructor(view: EditorView) {
        this.placeholders = widgetMatcher.createDeco(view);
      }
      update(update: ViewUpdate) {
        // A full rebuild when the caret moves, because whether a tag shows as
        // a chip now depends on where the caret is, and the incremental path
        // only reacts to edits. The composer holds a message, not a file, so
        // rebuilding it is cheap.
        this.placeholders = update.selectionSet
          ? widgetMatcher.createDeco(update.view)
          : widgetMatcher.updateDeco(update, this.placeholders);
      }
    },
    {
      decorations: (instance) => instance.placeholders,
      provide: (plugin) =>
        EditorView.atomicRanges.of((view) => {
          return view.plugin(plugin)?.placeholders || Decoration.none;
        }),
    },
  );

  return [widgetsPlugin, draftPlugin, widgetsTheme];
}

/** Whether a caret or selection sits anywhere in the given span. */
function touchesSelection(view: EditorView, from: number, to: number) {
  return view.state.selection.ranges.some(
    (range) => range.to >= from && range.from <= to,
  );
}

/**
 * A tag as it is being written.
 *
 * Marks, not a widget: the text under it is what the caret is in the middle of
 * editing, so it has to stay text. The kind takes a darker fill on the left,
 * what is being typed keeps the lighter one on the right, and a border runs
 * around the pair - so a half-written tag already looks like the chip it is
 * about to become.
 */
const RE_gameTagDraft = /@([a-zA-Z]+)([ \t]+[a-zA-Z-]*)?/g;

const draftMark = Decoration.mark({ class: "cm-gametag-draft" });
const draftKindMark = Decoration.mark({ class: "cm-gametag-draft-kind" });

function buildDraft(view: EditorView) {
  const marks: { from: number; to: number; value: Decoration }[] = [];

  for (const range of view.state.selection.ranges) {
    const line = view.state.doc.lineAt(range.head);
    const column = range.head - line.from;

    RE_gameTagDraft.lastIndex = 0;
    for (const match of line.text.matchAll(RE_gameTagDraft)) {
      const start = match.index!;
      const end = start + match[0].length;

      // Only the one being written, and only if `@…` names a kind we have.
      if (column < start || column > end) continue;
      if (!isGameTagKind(match[1])) continue;
      if (isInCodeBlock(view.state, line.from + start, line.from + end)) {
        continue;
      }

      marks.push({
        from: line.from + start,
        to: line.from + end,
        value: draftMark,
      });
      marks.push({
        from: line.from + start,
        to: line.from + start + match[1].length + 1,
        value: draftKindMark,
      });
    }
  }

  // Sorted by start, then by width: the outer mark has to be added before the
  // inner one it contains, or the range set rejects the pair.
  marks.sort((a, b) => a.from - b.from || b.to - a.to);

  return Decoration.set(
    marks.map(({ from, to, value }) => value.range(from, to)),
  );
}

const draftPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = buildDraft(view);
    }

    update(update: ViewUpdate) {
      if (update.docChanged || update.selectionSet || update.viewportChanged) {
        this.decorations = buildDraft(update.view);
      }
    }
  },
  {
    decorations: (instance) => instance.decorations,
  },
);

const widgetsTheme = EditorView.theme({
  ".cm-emoji-widget span": {
    display: "inline-block",
    width: "auto",
    height: "1lh",
    "vertical-align": "bottom",
  },
  ".cm-emoji-widget img": {
    display: "inline-block",
    width: "1.375em",
    height: "1.375em",
    "vertical-align": "baseline",
    "margin-bottom": "-0.375em",
    "object-fit": "contain",
  },

  ".cm-mention-widget": {
    "vertical-align": "bottom",

    gap: "4px",
    "padding-left": "2px",
    "padding-right": "6px",
    "align-items": "center",
    display: "inline-flex",

    height: "1.4em", // same as line-height

    "font-weight": 600,
    "border-radius": "var(--borderRadius-lg)",

    color: "var(--md-sys-color-on-primary-container)",
    background: "var(--md-sys-color-primary-container)",
  },

  ".cm-mention-widget img": {
    width: "16px",
    height: "16px",
    objectFit: "cover",
    borderRadius: "var(--borderRadius-full)",
  },

  // Squarer and quieter than a mention: this names a thing, not a person, and
  // it should not read as though somebody has been pinged.
  ".cm-gametag-widget": {
    "vertical-align": "bottom",

    gap: "6px",
    "padding-left": "4px",
    "padding-right": "6px",
    "align-items": "center",
    display: "inline-flex",

    height: "1.4em", // same as line-height

    "font-weight": 500,
    "border-radius": "var(--borderRadius-xs)",

    color: "var(--md-sys-color-on-surface)",
    background: "var(--md-sys-color-surface-container-highest)",
    border: "1px solid var(--md-sys-color-outline-variant)",
  },

  ".cm-gametag-widget img": {
    width: "16px",
    height: "16px",
    objectFit: "contain",
    "-webkit-user-drag": "none",
  },

  // The right half is where the name is typed, and before anything is typed it
  // is one space wide - which is to say invisible. The trailing padding gives
  // it a body of its own, so the chip shows both halves from the moment the
  // kind is chosen and the caret sits in a space that looks like a field.
  ".cm-gametag-draft": {
    "border-radius": "var(--borderRadius-xs)",
    border: "1px solid var(--md-sys-color-outline-variant)",
    background: "var(--md-sys-color-surface-container-highest)",

    "padding-block": "2px",
    "padding-inline-end": "14px",
  },

  ".cm-gametag-draft-kind": {
    "font-weight": 600,
    color: "var(--md-sys-color-on-surface-variant)",
    background: "var(--md-sys-color-surface-container-low)",

    // Rounded only on the outer side, so the two halves meet flat and read as
    // one chip cut in two rather than a pill inside a box.
    "border-radius": "var(--borderRadius-xs) 0 0 var(--borderRadius-xs)",
    "padding-inline": "8px",
    "padding-block": "2px",

    // Keeps the caret off the fill it sits next to.
    "margin-inline-end": "8px",
  },
});

class GameTagWidget extends WidgetType {
  private readonly tag: GameTag;

  constructor(tag: GameTag) {
    super();
    this.tag = tag;
  }

  eq(other: GameTagWidget): boolean {
    return this.tag.kind === other.tag.kind && this.tag.slug === other.tag.slug;
  }

  toDOM() {
    const outer = document.createElement("span");
    const img = document.createElement("img");

    outer.classList.add("cm-gametag-widget");
    img.src = gameTagUrl(this.tag);
    img.alt = "";
    // Otherwise dragging it lands on the "drop file to send" overlay.
    img.draggable = false;

    outer.appendChild(img);
    outer.appendChild(document.createTextNode(this.tag.label));

    return outer;
  }
}

class EmojiWidget extends WidgetType {
  private readonly url: string;

  constructor(url: string) {
    super();
    this.url = url;
  }

  eq(other: EmojiWidget): boolean {
    return this.url === other.url;
  }

  toDOM() {
    const outer = document.createElement("span");
    const inner = document.createElement("span");
    const img = document.createElement("img");
    outer.classList.add("cm-emoji-widget");
    img.src = this.url;
    inner.appendChild(img);
    outer.appendChild(inner);
    return outer;
  }
}

class UserMentionWidget extends WidgetType {
  private readonly user: User | undefined;
  private readonly member: ServerMember | undefined;

  constructor(user?: User, member?: ServerMember) {
    super();
    this.user = user;
    this.member = member;
  }

  eq(other: UserMentionWidget): boolean {
    return this.user === other.user && this.member === other.member;
  }

  toDOM() {
    const mention = document.createElement("span");
    mention.classList.add("cm-mention-widget");
    mention.contentEditable = "false";

    const { username, avatar } = userInformation(this.user, this.member);

    if (avatar) {
      const image = document.createElement("img");
      image.src = avatar;
      mention.appendChild(image);
    } else {
      const icon = document.createElement("span");
      icon.innerText = "at";
      icon.classList.add("material-symbols-outlined");
      mention.appendChild(icon);
    }

    const name = document.createElement("span");
    name.textContent = username;
    mention.appendChild(name);

    return mention;
  }
}

class ChannelMentionWidget extends WidgetType {
  private readonly channel: Channel;

  constructor(channel: Channel) {
    super();
    this.channel = channel;
  }

  eq(other: ChannelMentionWidget): boolean {
    return this.channel === other.channel;
  }

  toDOM() {
    const mention = document.createElement("span");
    mention.classList.add("cm-mention-widget");
    mention.contentEditable = "false";

    const icon = document.createElement("span");
    icon.innerText = "tag";
    icon.classList.add("material-symbols-outlined");
    mention.appendChild(icon);

    const name = document.createElement("span");
    name.textContent = this.channel.name;
    mention.appendChild(name);

    return mention;
  }
}

class RoleMentionWidget extends WidgetType {
  private readonly role: ServerRole;

  constructor(role: ServerRole) {
    super();
    this.role = role;
  }

  eq(other: RoleMentionWidget): boolean {
    return this.role === other.role;
  }

  toDOM() {
    const span = document.createElement("span");
    span.classList.add("cm-mention-widget");
    span.contentEditable = "false";
    span.textContent = this.role.name;
    return span;
  }
}
